import React, { useEffect, useState } from 'react';
import { FiPlus, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import SearchSelect from '../components/SearchSelect/SearchSelect';
import p from './Pages.module.css';

const TZ_OPTIONS = [
  '-12:00','-11:00','-10:00','-09:00','-08:00','-07:00','-06:00','-05:00',
  '-04:00','-03:00','-02:00','-01:00','+00:00','+01:00','+02:00','+03:00',
  '+04:00','+05:00','+05:30','+06:00','+07:00','+08:00','+09:00','+10:00','+11:00','+12:00',
];

const SIDES = [{ value: 'Home', label: 'Home' }, { value: 'Away', label: 'Away' }];

// Generate line options as [0.5, 1.0, 1.5, ...] within a range
const genLines = (min, max, step = 0.5) => {
  const arr = [];
  for (let v = min; v <= max; v += step) arr.push(parseFloat(v.toFixed(1)));
  return arr;
};

// Dynamic O/U line configs per stat category
const STAT_OU_CONFIG = {
  corners:   { label: 'Corners FT',  prefix: 'cornersOu', lineOpts: genLines(7.5, 13.5), max: 2 },
  htCorners: { label: 'Corners 1H',  prefix: 'htCornersOu', lineOpts: genLines(3.5, 7.5), max: 2 },
  yc:        { label: 'Yellow Cards FT', prefix: 'ycOu', lineOpts: genLines(2.5, 6.5), max: 2 },
  htYc:      { label: 'Yellow Cards 1H', prefix: 'htYcOu', lineOpts: genLines(0.5, 3.5), max: 2 },
  sot:       { label: 'Shots on Target FT', prefix: 'sotOu', lineOpts: genLines(7.5, 13.5), max: 2 },
  htSot:     { label: 'Shots on Target 1H', prefix: 'htSotOu', lineOpts: genLines(3.5, 7.5), max: 2 },
};

// Stat category 1x2 field configs
const STAT_1X2 = [
  { cat: 'corners',   label: 'Corners FT — 1X2',  keys: ['cornersR1', 'cornersRx', 'cornersR2'] },
  { cat: 'htCorners', label: 'Corners 1H — 1X2',  keys: ['htCornersR1', 'htCornersRx', 'htCornersR2'] },
  { cat: 'yc',        label: 'Yellow Cards FT — 1X2', keys: ['ycR1', 'ycRx', 'ycR2'] },
  { cat: 'htYc',      label: 'Yellow Cards 1H — 1X2', keys: ['htYcR1', 'htYcRx', 'htYcR2'] },
  { cat: 'sot',       label: 'Shots on Target FT — 1X2', keys: ['sotR1', 'sotRx', 'sotR2'] },
  { cat: 'htSot',     label: 'Shots on Target 1H — 1X2', keys: ['htSotR1', 'htSotRx', 'htSotR2'] },
];

// Team TT stat fields per side
const TEAM_STAT_TT_FIELDS = [
  { lineKey: 'cornersTtLine', oKey: 'cornersTtO', uKey: 'cornersTtU', label: 'Corners TT FT' },
  { lineKey: 'htCornersTtLine', oKey: 'htCornersTtO', uKey: 'htCornersTtU', label: 'Corners TT 1H' },
  { lineKey: 'ycTtLine', oKey: 'ycTtO', uKey: 'ycTtU', label: 'YC TT FT' },
  { lineKey: 'htYcTtLine', oKey: 'htYcTtO', uKey: 'htYcTtU', label: 'YC TT 1H' },
  { lineKey: 'sotTtLine', oKey: 'sotTtO', uKey: 'sotTtU', label: 'SOT TT FT' },
  { lineKey: 'htSotTtLine', oKey: 'htSotTtO', uKey: 'htSotTtU', label: 'SOT TT 1H' },
];

function Field({ label, value, onChange, width = '140px' }) {
  return (
    <div className={p.fieldGroup}>
      <label className={p.fieldLabel}>{label}</label>
      <input className={p.fieldInput} style={{ width }} type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} />
    </div>
  );
}

// Collapsible section wrapper
function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={p.sectionCard}>
      <div className={p.sectionCardHeader} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setOpen(v => !v)}>
        <h3 className={p.sectionCardTitle} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
          {title}
        </h3>
      </div>
      {open && <div className={p.sectionCardBody}>{children}</div>}
    </div>
  );
}

// Dynamic O/U line selector component
function DynamicOuLines({ catKey, config, lines, onAdd, onRemove, onChangeField }) {
  const [selected, setSelected] = useState('');
  const usedLines = lines.map(l => l.line);
  const available = config.lineOpts.filter(v => !usedLines.includes(v));
  const canAdd = lines.length < config.max && available.length > 0;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Add line control */}
      {canAdd && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <select
            className={p.fieldInput}
            style={{ width: 120, appearance: 'auto' }}
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            <option value="">Select line…</option>
            {available.map(v => <option key={v} value={v}>O/U {v}</option>)}
          </select>
          <button
            className={p.btnOutline}
            style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}
            disabled={!selected}
            onClick={() => { if (selected) { onAdd(catKey, parseFloat(selected)); setSelected(''); } }}
          >
            <FiPlus size={13} /> Add Line
          </button>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {lines.length}/{config.max} lines
          </span>
        </div>
      )}
      {/* Added lines */}
      {lines.map((entry, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 8, padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent)', minWidth: 70, paddingBottom: 6 }}>
            O/U {entry.line}
          </div>
          <Field label="Over" value={entry.over} onChange={v => onChangeField(catKey, idx, 'over', v)} width="100px" />
          <Field label="Under" value={entry.under} onChange={v => onChangeField(catKey, idx, 'under', v)} width="100px" />
          <button
            className={p.btnOutline}
            style={{ padding: '6px 8px', color: '#e06c75', borderColor: 'rgba(224,108,117,0.3)', marginBottom: 0 }}
            onClick={() => onRemove(catKey, idx)}
            title="Remove line"
          >
            <FiX size={14} />
          </button>
        </div>
      ))}
      {lines.length === 0 && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0' }}>
          No O/U lines added yet — select a line and click Add
        </div>
      )}
    </div>
  );
}

export default function CreateMatch() {
  const api = useApi();
  const toast = useToast();

  const [teamSides, setTeamSides] = useState(SIDES);
  const [clubs, setClubs] = useState([]);
  const [clubsFetched, setClubsFetched] = useState(false);

  // League — required, with create-on-Enter behavior matching the Settings page.
  const [leagues, setLeagues] = useState([]);
  const [leaguesFetched, setLeaguesFetched] = useState(false);
  const [leagueId, setLeagueId] = useState('');

  const [sideA, setSideA] = useState('Home');
  const [sideB, setSideB] = useState('Away');
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [matchTime, setMatchTime] = useState('');
  const [timezone, setTimezone] = useState('+01:00');

  // Team-level fields (object per side)
  const emptyTeamFields = () => ({
    ttft15o: '', ttft15u: '', ttht05o: '', ttht05u: '', ttsh05o: '', ttsh05u: '',
    cornersTtLine: '', cornersTtO: '', cornersTtU: '',
    htCornersTtLine: '', htCornersTtO: '', htCornersTtU: '',
    ycTtLine: '', ycTtO: '', ycTtU: '',
    htYcTtLine: '', htYcTtO: '', htYcTtU: '',
    sotTtLine: '', sotTtO: '', sotTtU: '',
    htSotTtLine: '', htSotTtO: '', htSotTtU: '',
  });
  const [teamFieldsA, setTeamFieldsA] = useState(emptyTeamFields());
  const [teamFieldsB, setTeamFieldsB] = useState(emptyTeamFields());

  // Quota fields (flat object) — only keys in REAL_QUOTA_DTO_TO_COL (backend rejects alt goal O/U lines).
  const emptyQuota = () => ({
    rh: '', rd: '', ra: '',
    ou25o: '', ou25u: '',
    rhth: '', rhtd: '', rhta: '',
    htou15o: '', htou15u: '',
    shR1: '', shRx: '', shR2: '',
    shOu15o: '', shOu15u: '',
    cornersR1: '', cornersRx: '', cornersR2: '',
    htCornersR1: '', htCornersRx: '', htCornersR2: '',
    ycR1: '', ycRx: '', ycR2: '',
    htYcR1: '', htYcRx: '', htYcR2: '',
    sotR1: '', sotRx: '', sotR2: '',
    htSotR1: '', htSotRx: '', htSotR2: '',
  });
  const [quota, setQuota] = useState(emptyQuota());
  const setQ = (key, val) => setQuota(prev => ({ ...prev, [key]: val }));

  // Dynamic O/U lines state: { corners: [{ line: 9.5, over: '', under: '' }, ...], ... }
  const emptyOuLines = () => ({
    corners: [], htCorners: [], yc: [], htYc: [], sot: [], htSot: [],
  });
  const [ouLines, setOuLines] = useState(emptyOuLines());

  const handleAddLine = (catKey, lineVal) => {
    setOuLines(prev => ({
      ...prev,
      [catKey]: [...prev[catKey], { line: lineVal, over: '', under: '' }],
    }));
  };

  const handleRemoveLine = (catKey, idx) => {
    setOuLines(prev => ({
      ...prev,
      [catKey]: prev[catKey].filter((_, i) => i !== idx),
    }));
  };

  const handleChangeLineField = (catKey, idx, field, value) => {
    setOuLines(prev => ({
      ...prev,
      [catKey]: prev[catKey].map((entry, i) => i === idx ? { ...entry, [field]: value } : entry),
    }));
  };

  useEffect(() => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    setMatchTime(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
    api.get('/v1/team-side/').then(data => {
      if (data && Array.isArray(data)) setTeamSides(data.map(t => ({ value: t.code, label: t.code })));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClubs = async () => {
    if (clubsFetched) return;
    try {
      const data = await api.get('/v1/club/');
      setClubs(data); setClubsFetched(true);
    } catch {}
  };

  const fetchLeagues = async (force = false) => {
    if (leaguesFetched && !force) return;
    try {
      const data = await api.get('/v1/league/');
      setLeagues(Array.isArray(data) ? data : []); setLeaguesFetched(true);
    } catch {}
  };

  const createLeagueInline = async (name) => {
    try {
      const r = await api.post('/v1/league/', { name });
      toast.success(`League “${r?.name || name}” created`);
      if (r?.id != null) {
        setLeagues(prev => [...prev, r]);
        setLeagueId(r.id);
        return { value: r.id, label: `${r.name} [${r.code}]` };
      }
      await fetchLeagues(true);
    } catch (e) {
      toast.error(e.message || 'Failed to create league');
    }
  };

  const clearFields = () => {
    setTeamA(''); setTeamB(''); setSideA('Home'); setSideB('Away');
    setLeagueId('');
    setTeamFieldsA(emptyTeamFields()); setTeamFieldsB(emptyTeamFields());
    setQuota(emptyQuota()); setOuLines(emptyOuLines());
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    setMatchTime(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
  };

  // Serialize dynamic O/U lines into quota keys for API: cornersOuLine1, cornersOu1o, cornersOu1u, etc.
  // Only emit a row when both over & under are filled (optional markets must be omitted, not nulled).
  const serializeOuLines = () => {
    const result = {};
    const prefixMap = {
      corners: 'cornersOu', htCorners: 'htCornersOu',
      yc: 'ycOu', htYc: 'htYcOu',
      sot: 'sotOu', htSot: 'htSotOu',
    };
    const toNum = (v) => {
      if (v === '' || v == null) return null;
      const n = Number(String(v).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    for (const [catKey, lines] of Object.entries(ouLines)) {
      const pre = prefixMap[catKey];
      let n = 0;
      lines.forEach((entry) => {
        if (entry.over === '' || entry.over == null || entry.under === '' || entry.under == null) return;
        n += 1;
        result[`${pre}Line${n}`] = toNum(entry.line);
        result[`${pre}${n}o`] = toNum(entry.over);
        result[`${pre}${n}u`] = toNum(entry.under);
      });
    }
    return result;
  };

  // Per FRONTEND_MARKET_OPT_IN.md — only these are required; everything else is optional and must be omitted (not nulled) when empty.
  const REQUIRED_QUOTA_KEYS = ['rh','rd','ra','ou25o','ou25u','rhth','rhtd','rhta','htou15o','htou15u'];
  const REQUIRED_TEAM_TT_KEYS = ['ttft15o','ttft15u','ttht05o','ttht05u','ttsh05o','ttsh05u'];

  const createMatch = async () => {
    const isFilled = (v) => !(v === '' || v == null);
    // Locale-tolerant numeric parse: accept comma-decimal ("1,15") as well as dot.
    const toNum = (v) => {
      if (v === '' || v == null) return null;
      const n = Number(String(v).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    // League is now required — see FRONTEND_CLUBS_CRUD_AND_SOFT_DELETE.md.
    if (!leagueId) { toast.error('League is required'); return; }
    // Validate required
    const missingQuota = REQUIRED_QUOTA_KEYS.filter(k => !isFilled(quota[k]));
    if (missingQuota.length) { toast.error(`Required match odds missing: ${missingQuota.join(', ')}`); return; }
    for (const [label, fields] of [['Team A', teamFieldsA], ['Team B', teamFieldsB]]) {
      const missing = REQUIRED_TEAM_TT_KEYS.filter(k => !isFilled(fields[k]));
      if (missing.length) { toast.error(`${label} required TT odds missing: ${missing.join(', ')}`); return; }
    }

    // Build quota: required keys always, optional keys only when filled.
    const quotaPayload = {};
    for (const [k, v] of Object.entries(quota)) {
      if (REQUIRED_QUOTA_KEYS.includes(k) || isFilled(v)) quotaPayload[k] = toNum(v);
    }
    // Dynamic OU lines — only include rows where both over & under are filled.
    Object.assign(quotaPayload, serializeOuLines());

    const buildTeam = (name, side, fields) => {
      const t = { name, side };
      for (const [k, v] of Object.entries(fields)) {
        if (REQUIRED_TEAM_TT_KEYS.includes(k) || isFilled(v)) t[k] = toNum(v);
      }
      // result block omitted on create — settlement fields are computed server-side.
      return t;
    };

    try {
      await api.post('/v1/match/', {
        matchTime: matchTime ? `${matchTime}:00${timezone}` : null,
        leagueId,
        teams: [
          buildTeam(teamA, sideA, teamFieldsA),
          buildTeam(teamB, sideB, teamFieldsB),
        ],
        quota: quotaPayload,
      });
      toast.success('Match created successfully');
      clearFields();
    } catch (err) {
      toast.error(err.message || 'Failed to create match');
    }
  };

  const renderTeamSection = (label, side, teamName, setTeamName, sideVal, setSideVal, fields, setFields) => (
    <CollapsibleSection title={label} defaultOpen={true}>
      <div className={p.inlineFields}>
        <div className={p.fieldGroup}>
          <label className={p.fieldLabel}>Side</label>
          <SearchSelect options={teamSides} value={sideVal} onChange={setSideVal} placeholder="Side" width="120px" />
        </div>
        <div className={p.fieldGroup}>
          <label className={p.fieldLabel}>Club</label>
          <SearchSelect options={clubs.map(c => ({ value: c.name, label: `${c.name} [${c.code}]` }))} value={teamName} onChange={setTeamName} placeholder="Select club" width="220px" onOpen={fetchClubs} />
        </div>
        <Field label="FT TT 1.5 O" value={fields.ttft15o} onChange={v => setFields(prev => ({ ...prev, ttft15o: v }))} />
        <Field label="FT TT 1.5 U" value={fields.ttft15u} onChange={v => setFields(prev => ({ ...prev, ttft15u: v }))} />
        <Field label="HT TT 0.5 O" value={fields.ttht05o} onChange={v => setFields(prev => ({ ...prev, ttht05o: v }))} />
        <Field label="HT TT 0.5 U" value={fields.ttht05u} onChange={v => setFields(prev => ({ ...prev, ttht05u: v }))} />
        <Field label="2H TT 0.5 O" value={fields.ttsh05o} onChange={v => setFields(prev => ({ ...prev, ttsh05o: v }))} />
        <Field label="2H TT 0.5 U" value={fields.ttsh05u} onChange={v => setFields(prev => ({ ...prev, ttsh05u: v }))} />
      </div>
      {/* Stat TT fields — each market group (Corners FT/1H, YC FT/1H, SOT FT/1H)
       * gets its own row with [Line, Over, Under] triplet for easier data entry. */}
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Stat Team Totals</div>
        {TEAM_STAT_TT_FIELDS.map(f => (
          <div key={f.lineKey} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{f.label}</div>
            <div className={p.inlineFields}>
              <Field label="Line" value={fields[f.lineKey]} onChange={v => setFields(prev => ({ ...prev, [f.lineKey]: v }))} width="90px" />
              <Field label="Over" value={fields[f.oKey]} onChange={v => setFields(prev => ({ ...prev, [f.oKey]: v }))} width="90px" />
              <Field label="Under" value={fields[f.uKey]} onChange={v => setFields(prev => ({ ...prev, [f.uKey]: v }))} width="90px" />
            </div>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Create Match</h1>
        <p className={p.pageSubtitle}>Set up a new match with teams, quotas, and scheduling</p>
      </div>

      {/* League — required. Mirrors the creatable dropdown used in Settings → Entities. */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>League *</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields}>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>League</label>
              <SearchSelect
                width="320px"
                placeholder="Select or type to create…"
                searchPlaceholder="Type league name…"
                options={leagues.map(l => ({ value: l.id, label: `${l.name} [${l.code}]` }))}
                value={leagueId}
                onChange={setLeagueId}
                onOpen={() => fetchLeagues()}
                onCreate={createLeagueInline}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Match Time */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>Schedule</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields}>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Date & Time</label>
              <input className={p.fieldInput} style={{ width: 220 }} type="datetime-local" value={matchTime} onChange={(e) => setMatchTime(e.target.value)} />
            </div>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Timezone</label>
              <select className={`${p.fieldInput}`} style={{ width: 140, appearance: 'none' }} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>UTC{tz}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Team A */}
      {renderTeamSection('Team A', 'home', teamA, setTeamA, sideA, setSideA, teamFieldsA, setTeamFieldsA)}

      {/* Team B */}
      {renderTeamSection('Team B', 'away', teamB, setTeamB, sideB, setSideB, teamFieldsB, setTeamFieldsB)}

      {/* Quotas — FT */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>Odds — FT Goals</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields} style={{ marginBottom: 12 }}>
            <Field label="Home 1x2" value={quota.rh} onChange={v => setQ('rh', v)} />
            <Field label="Draw 1x2" value={quota.rd} onChange={v => setQ('rd', v)} />
            <Field label="Away 1x2" value={quota.ra} onChange={v => setQ('ra', v)} />
          </div>
          {[
            { line: '2.5', oKey: 'ou25o', uKey: 'ou25u' },
          ].map(r => (
            <div key={r.line} className={p.inlineFields} style={{ marginBottom: 8 }}>
              <Field label={`O/U ${r.line} O`} value={quota[r.oKey]} onChange={v => setQ(r.oKey, v)} />
              <Field label={`O/U ${r.line} U`} value={quota[r.uKey]} onChange={v => setQ(r.uKey, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Quotas — HT */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>Odds — HT Goals</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields} style={{ marginBottom: 12 }}>
            <Field label="Home 1x2" value={quota.rhth} onChange={v => setQ('rhth', v)} />
            <Field label="Draw 1x2" value={quota.rhtd} onChange={v => setQ('rhtd', v)} />
            <Field label="Away 1x2" value={quota.rhta} onChange={v => setQ('rhta', v)} />
          </div>
          {[
            { line: '1.5', oKey: 'htou15o', uKey: 'htou15u' },
          ].map(r => (
            <div key={r.line} className={p.inlineFields} style={{ marginBottom: 8 }}>
              <Field label={`O/U ${r.line} O`} value={quota[r.oKey]} onChange={v => setQ(r.oKey, v)} />
              <Field label={`O/U ${r.line} U`} value={quota[r.uKey]} onChange={v => setQ(r.uKey, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Quotas — 2H Goals */}
      <CollapsibleSection title="Odds — 2nd Half Goals">
        <div className={p.inlineFields} style={{ marginBottom: 12 }}>
          <Field label="Home 1x2" value={quota.shR1} onChange={v => setQ('shR1', v)} />
          <Field label="Draw 1x2" value={quota.shRx} onChange={v => setQ('shRx', v)} />
          <Field label="Away 1x2" value={quota.shR2} onChange={v => setQ('shR2', v)} />
        </div>
        {[
          { line: '1.5', oKey: 'shOu15o', uKey: 'shOu15u' },
        ].map(r => (
          <div key={r.line} className={p.inlineFields} style={{ marginBottom: 8 }}>
            <Field label={`O/U ${r.line} O`} value={quota[r.oKey]} onChange={v => setQ(r.oKey, v)} />
            <Field label={`O/U ${r.line} U`} value={quota[r.uKey]} onChange={v => setQ(r.uKey, v)} />
          </div>
        ))}
      </CollapsibleSection>

      {/* Stat category sections with dynamic O/U */}
      {STAT_1X2.map(({ cat, label, keys }) => (
        <CollapsibleSection key={cat} title={`Odds — ${STAT_OU_CONFIG[cat].label}`}>
          {/* 1X2 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>1X2</div>
            <div className={p.inlineFields}>
              <Field label="Home" value={quota[keys[0]]} onChange={v => setQ(keys[0], v)} />
              <Field label="Draw" value={quota[keys[1]]} onChange={v => setQ(keys[1], v)} />
              <Field label="Away" value={quota[keys[2]]} onChange={v => setQ(keys[2], v)} />
            </div>
          </div>
          {/* Dynamic O/U lines */}
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Over/Under Lines</div>
          <DynamicOuLines
            catKey={cat}
            config={STAT_OU_CONFIG[cat]}
            lines={ouLines[cat]}
            onAdd={handleAddLine}
            onRemove={handleRemoveLine}
            onChangeField={handleChangeLineField}
          />
        </CollapsibleSection>
      ))}

      <div style={{ paddingBottom: 60 }}>
        <button className={p.btnPrimary} onClick={createMatch}>Create Match</button>
      </div>
    </div>
  );
}

